import { useState, useEffect } from "react";
import { Modal, Form, InputNumber, Input, Select, DatePicker, Typography, message, Alert, Space, Tag } from "antd";
import { useCompany } from "../contexts/CompanyContext";
import { useConfig } from "../contexts/ConfigContext";
import { useRegister } from "../contexts/RegisterContext";
import { useSecureRequest } from "../hooks/useSecureRequest";
import { IMovementRS } from "../models/storageModel";
import { DollarOutlined } from "@ant-design/icons";
import { textNumberFormater } from "../utils";
import dayjs from "dayjs";
import { TreasuryAccountItem } from "@ryo/ui";

const { Text } = Typography;

interface RegisterMovementPaymentModalProps {
  visible: boolean;
  onClose: () => void;
  movement: Partial<IMovementRS> | null;
  onSuccess: () => void;
}

export default function RegisterMovementPaymentModal({
  visible,
  onClose,
  movement,
  onSuccess,
}: Readonly<RegisterMovementPaymentModalProps>) {
  const { company } = useCompany();
  const { config } = useConfig();
  const { register } = useRegister();
  const { securePost } = useSecureRequest();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [lockDate, setLockDate] = useState<string | null>(null);
  const [treasuryAccounts, setTreasuryAccounts] = useState<TreasuryAccountItem[]>([]);

  useEffect(() => {
    if (visible && (company.current?.id || company.current?.uid)) {
      securePost("/accounting/treasury-accounts/get-accounts", {
        company_id: company.current?.id,
        company: company.current?.uid,
      })
        .then((res) => {
          if (res?.data && Array.isArray(res.data)) {
            setTreasuryAccounts(res.data);
          }
        })
        .catch(() => null);

      securePost("/accounting/period-lock", {
        company_id: company.current?.id,
        company: company.current?.uid,
      })
        .then((res) => {
          const lk = res?.data?.lock?.lock_date || res?.data?.lock_date;
          if (lk) {
            setLockDate(String(lk).substring(0, 10));
          } else {
            setLockDate(null);
          }
        })
        .catch(() => null);
    }
  }, [visible, company.current?.id, company.current?.uid]);

  const beforeTaxes = Number(movement?.total_amount || 0);
  const totalTax = Number(movement?.total_tax_amount || 0);
  const itemsDiscount = (movement?.item_list || []).reduce((acc: number, it: any) => acc + (Number(it.discount_amount) || 0), 0);
  const totalDiscount = Number(movement?.total_discount_amount ?? itemsDiscount);
  const calculatedGross = Math.round(((beforeTaxes + totalTax - totalDiscount) + Number.EPSILON) * 100) / 100;
  const docTotal = calculatedGross > 0 ? calculatedGross : Number(movement?.total_amount_net || beforeTaxes);
  const totalPaid = Number(movement?.total_paid_amount || 0);
  const balanceDue = totalPaid >= docTotal ? 0 : Math.max(0, Math.round(((docTotal - totalPaid) + Number.EPSILON) * 100) / 100);

  const isPurchase = movement?.document_type === "PURCHASE_RECEIPT";
  const voucherName = isPurchase ? "Comprobante de Egreso (CE)" : "Recibo de Caja (RC)";

  useEffect(() => {
    if (visible && movement) {
      form.setFieldsValue({
        amount: balanceDue > 0 ? balanceDue : undefined,
        payment_method: "TRANSFER",
        treasury_account_id: undefined,
        payment_date: dayjs().startOf('minute'),
        reference: "",
        notes: "",
      });
    }
  }, [visible, movement, balanceDue, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!movement?.id) {
        message.error("ID de movimiento inválido");
        return;
      }

      const paymentDateStr = values.payment_date
        ? dayjs(values.payment_date).startOf('minute').format()
        : dayjs().startOf('minute').format();

      setLoading(true);
      const res = await securePost("/inventory/movement/register-payment", {
        company: company.current.uid,
        document_id: movement.id,
        movement_id: movement.id,
        amount: Number(values.amount),
        payment: {
          amount: Number(values.amount),
          payment_method: values.payment_method,
          treasury_account_id: values.treasury_account_id || undefined,
          reference: values.reference || undefined,
          payment_date: paymentDateStr,
          notes: values.notes || undefined,
        },
        payment_method: values.payment_method,
        treasury_account_id: values.treasury_account_id || undefined,
        reference: values.reference || undefined,
        payment_date: paymentDateStr,
        notes: values.notes || undefined,
        module: config.module,
      });

      if (res.status === 200 || res.success) {
        message.success(res.message || "Abono / Pago registrado exitosamente");
        form.resetFields();
        onSuccess();
        onClose();
      } else {
        message.error(res.message || "Error al registrar el abono");
      }
    } catch (err: any) {
      if (err?.errorFields) return; // Antd validation
      console.error("Error in register-payment:", err);
      message.error(err?.message || "Error al registrar el pago");
    } finally {
      setLoading(false);
    }
  };

  const paymentOptions = (register.paymentOptions && register.paymentOptions.length > 0)
    ? register.paymentOptions
    : [
        { id: 1, key: "TRANSFER", string_value: "Transferencia Bancaria" },
        { id: 2, key: "CASH", string_value: "Efectivo" },
        { id: 3, key: "CARD", string_value: "Tarjeta Débito/Crédito" },
        { id: 4, key: "CREDIT_CARD", string_value: "Tarjeta de Crédito" },
        { id: 5, key: "DEBIT_CARD", string_value: "Tarjeta Débito" },
        { id: 6, key: "NEQUI", string_value: "Nequi" },
        { id: 7, key: "DAVIPLATA", string_value: "Daviplata" },
      ];

  return (
    <Modal
      title={
        <Space>
          <DollarOutlined style={{ color: "#52c41a", fontSize: 20 }} />
          <span>Registrar Abono / Pago de Factura</span>
          <Text type="secondary" style={{ fontSize: 13 }}>
            ({movement?.document_number || "Doc"})
          </Text>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText="Registrar Abono y Generar Comprobante"
      cancelText="Cancelar"
      width={560}
    >
      <Alert
        message={`Asiento de Tesorería - ${voucherName}`}
        description={`El abono genera un movimiento contable de forma autónoma.`}
        type="success"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <div
        style={{
          background: "#fafafa",
          padding: "12px 16px",
          borderRadius: 8,
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div>
          <Text type="secondary">Total Documento:</Text>
          <div style={{ fontWeight: 600 }}>{textNumberFormater(docTotal)}</div>
        </div>
        <div>
          <Text type="secondary">Total Pagado a la Fecha:</Text>
          <div style={{ fontWeight: 600, color: "#52c41a" }}>{textNumberFormater(totalPaid)}</div>
        </div>
        <div>
          <Text type="secondary">Saldo Pendiente:</Text>
          <div style={{ fontWeight: 700, color: "#f5222d", fontSize: 15 }}>
            {textNumberFormater(balanceDue)}
          </div>
        </div>
      </div>

      <Form form={form} layout="vertical">
        <Form.Item
          label="Monto del Abono / Pago"
          name="amount"
          rules={[
            { required: true, message: "Por favor ingrese el monto" },
            {
              type: "number",
              min: 1,
              max: balanceDue > 0 ? balanceDue : undefined,
              message: `El monto no puede superar el saldo pendiente (${textNumberFormater(balanceDue)})`,
            },
          ]}
        >
          <InputNumber
            prefix="$"
            style={{ width: "100%" }}
            formatter={(val) => (val ? `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "")}
            parser={(val) => (val ? Number(val.replace(/\$\s?|(,*)/g, "")) : 0)}
            placeholder="Ingrese monto a pagar"
          />
        </Form.Item>

        <Form.Item
          label="Medio de Pago"
          name="payment_method"
          rules={[{ required: true, message: "Seleccione medio de pago" }]}
        >
          <Select placeholder="Seleccione medio de pago">
            {paymentOptions.map((opt) => (
              <Select.Option key={opt.key} value={opt.key}>
                {opt.string_value || opt.key}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label={
            <Space size={6} align="center">
              <span>Fecha del Pago</span>
              {lockDate && (
                <Tag color="error" bordered={false} style={{ fontSize: 10, lineHeight: "16px", padding: "0 4px", margin: 0 }}>
                  Cerrado: {dayjs(lockDate).format("DD/MM/YYYY")}
                </Tag>
              )}
            </Space>
          }
          name="payment_date"
          tooltip={lockDate ? `Periodo contable cerrado hasta el ${dayjs(lockDate).format("DD/MM/YYYY")}. Solo se permiten pagos con fecha posterior.` : undefined}
          rules={[
            { required: true, message: "Seleccione fecha de pago" },
            {
              validator: (_, value) => {
                if (value && lockDate) {
                  const valDay = dayjs(value);
                  const lockDay = dayjs(lockDate);
                  if (valDay.isBefore(lockDay, "day") || valDay.isSame(lockDay, "day")) {
                    return Promise.reject(
                      new Error(`El periodo contable hasta el ${lockDay.format("DD/MM/YYYY")} se encuentra cerrado.`)
                    );
                  }
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            disabledDate={(current) => {
              if (!lockDate || !current) return false;
              return current.isBefore(dayjs(lockDate), "day") || current.isSame(dayjs(lockDate), "day");
            }}
          />
        </Form.Item>

        <Form.Item
          label="No. de Soporte / Comprobante Bancario"
          name="reference"
          rules={[{ required: true, message: "Ingrese referencia bancaria o soporte" }]}
        >
          <Input placeholder="Ej: Transf. Bancolombia #83921 / Cheque #102" />
        </Form.Item>

        <Form.Item label="Observaciones o Notas de Tesorería" name="notes">
          <Input.TextArea rows={2} placeholder="Notas adicionales..." />
        </Form.Item>
      </Form>
    </Modal>
  );
}
